import React, { useState, useRef, useEffect } from 'react';
import { Download, Copy, FileJson, ChevronDown, Check } from 'lucide-react';
import useExportData from '../../hooks/useExportData';
import './ExportMenu.css';

/**
 * ExportMenu - Reusable dropdown menu for exporting table data
 *
 * Provides three export options:
 * 1. Download CSV - Downloads data as a .csv file
 * 2. Copy as Text - Copies tab-separated text (for pasting into Claude, docs, etc.)
 * 3. Copy as JSON - Copies formatted JSON
 *
 * @param {Object} props
 * @param {Array} props.data - Array of data objects to export
 * @param {Array} props.columns - Column definitions [{ key, label }]
 * @param {string} props.filename - Base filename for downloads
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.size - Button size: 'sm' | 'md' (default: 'sm')
 * @param {boolean} props.disabled - Disable the export menu
 */
const ExportMenu = ({
  data = [],
  columns = [],
  filename = 'export',
  className = '',
  size = 'sm',
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);
  const { downloadCSV, copyAsText, copyAsJSON, copyStatus } = useExportData({ filename });

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleExport = (type) => {
    switch (type) {
      case 'csv':
        downloadCSV(data, columns);
        break;
      case 'text':
        copyAsText(data, columns);
        break;
      case 'json':
        copyAsJSON(data, columns);
        break;
      default:
        break;
    }
    // Keep menu open briefly to show feedback, then close
    setTimeout(() => setIsOpen(false), 300);
  };

  const rowCount = data?.length || 0;

  return (
    <div className={`export-menu ${className}`} ref={menuRef}>
      <button
        className={`export-menu-btn export-menu-btn-${size}`}
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled || rowCount === 0}
        title={rowCount === 0 ? 'No data to export' : `Export ${rowCount} rows`}
      >
        <Download size={size === 'sm' ? 14 : 16} />
        <span>Export</span>
        <ChevronDown size={size === 'sm' ? 12 : 14} className={isOpen ? 'rotated' : ''} />
      </button>

      {isOpen && (
        <div className="export-menu-dropdown">
          <div className="export-menu-header">
            Export {rowCount} row{rowCount !== 1 ? 's' : ''}
          </div>

          <button
            className="export-menu-item"
            onClick={() => handleExport('csv')}
          >
            <Download size={14} />
            <span>Download CSV</span>
          </button>

          <button
            className="export-menu-item"
            onClick={() => handleExport('text')}
          >
            {copyStatus === 'text' ? (
              <Check size={14} className="export-success" />
            ) : (
              <Copy size={14} />
            )}
            <span>{copyStatus === 'text' ? 'Copied!' : 'Copy as Text'}</span>
            <span className="export-menu-hint">For Claude</span>
          </button>

          <button
            className="export-menu-item"
            onClick={() => handleExport('json')}
          >
            {copyStatus === 'json' ? (
              <Check size={14} className="export-success" />
            ) : (
              <FileJson size={14} />
            )}
            <span>{copyStatus === 'json' ? 'Copied!' : 'Copy as JSON'}</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default ExportMenu;
